import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import SignaturePad from '@/components/shared/SignaturePad';
import { AssetStatusBadge, ConditionBadge } from '@/components/shared/StatusBadge';
import {
  ArrowLeft, ClipboardCheck, FileSignature, CheckCircle2, Mail, Package,
} from 'lucide-react';
import { useWorkOrder, useIntakeLines, useWorkOrderAssets } from '@/hooks/useWorkOrders';
import { toast } from 'sonner';

export default function IntakeSummaryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [signerName, setSignerName] = useState('');
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const { data: wo, isLoading: woLoading } = useWorkOrder(id);
  const { data: intakeLines = [] } = useIntakeLines(id);
  const { data: assets = [] } = useWorkOrderAssets(id);

  if (woLoading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-40 w-full" /></div>;
  }

  if (!wo) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Work order not found.</p>
      </div>
    );
  }

  const totalReceived = intakeLines.reduce((sum, l) => sum + l.quantity_total, 0) || assets.length;
  const totalSerialized = intakeLines.reduce((sum, l) => sum + l.quantity_serialized, 0) || assets.length;

  function handleConfirm() {
    if (!signerName.trim() || !signatureData) return;
    // TODO: call API to store signature + update status
    setConfirmed(true);
    toast.success('Intake confirmed with signature');
  }

  function handleSendEmail() {
    toast.success('Confirmation email sent to ' + wo!.email);
  }

  if (confirmed) {
    return (
      <>
        <div className="mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/work-orders/${wo.id}`)} className="gap-1.5 text-muted-foreground">
            <ArrowLeft className="h-4 w-4" /> {wo.work_order_number}
          </Button>
        </div>

        <div className="max-w-2xl mx-auto text-center space-y-6 py-12">
          <div className="flex justify-center">
            <div className="h-16 w-16 rounded-full bg-status-complete/10 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-status-complete" />
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Intake Complete</h1>
            <p className="text-muted-foreground mt-2">
              {wo.work_order_number} · {wo.customer_name}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {totalReceived} items received · Signed by {signerName} · {new Date().toLocaleDateString()}
            </p>
          </div>
          <div className="flex justify-center gap-3">
            <Button onClick={handleSendEmail} className="gap-2">
              <Mail className="h-4 w-4" /> Send Confirmation Email
            </Button>
            <Button variant="outline" onClick={() => navigate(`/work-orders/${wo.id}`)}>
              View Work Order
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/work-orders/${wo.id}/intake`)} className="gap-1.5 text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Intake
        </Button>
      </div>

      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <ClipboardCheck className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold text-foreground">Intake Summary</h1>
            <p className="text-sm text-muted-foreground">{wo.work_order_number} · {wo.customer_name}</p>
          </div>
        </div>

        {/* Work Order Info */}
        <Card>
          <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Work Order</p>
              <p className="text-sm font-mono font-medium text-foreground">{wo.work_order_number}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Customer</p>
              <p className="text-sm font-medium text-foreground">{wo.customer_name}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Type</p>
              <p className="text-sm font-medium capitalize text-foreground">{wo.work_order_type}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Date</p>
              <p className="text-sm font-medium text-foreground">{new Date().toLocaleDateString()}</p>
            </div>
          </CardContent>
        </Card>

        {/* Grouped Intake Lines */}
        {intakeLines.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Package className="h-4 w-4 text-primary" /> Intake Lines Summary
              </p>
              <div className="space-y-3">
                {intakeLines.map((line) => {
                  const pct = line.quantity_total > 0 ? Math.round((line.quantity_serialized / line.quantity_total) * 100) : 0;
                  return (
                    <div key={line.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground">{line.category_name}</p>
                          <Badge variant="outline" className="text-[10px] capitalize">{line.intake_mode}</Badge>
                        </div>
                        <p className="text-sm font-mono text-foreground">{line.quantity_serialized}/{line.quantity_total}</p>
                      </div>
                      <Progress value={pct} className="h-1.5" />
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {line.quantity_remaining > 0
                          ? `${line.quantity_remaining} pending serialization`
                          : 'All items serialized'}
                      </p>
                    </div>
                  );
                })}
              </div>
              <Separator className="my-3" />
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">Total</p>
                <p className="text-sm font-mono font-semibold text-foreground">{totalSerialized}/{totalReceived} serialized</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Individual Items */}
        {assets.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-foreground mb-3">Individual Items ({assets.length})</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {assets.map((asset) => (
                  <div key={asset.id} className="flex items-center justify-between rounded border border-border p-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-mono font-medium text-foreground">{asset.internal_barcode}</p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {asset.category_name}
                        {asset.manufacturer_name && ` · ${asset.manufacturer_name}`}
                        {asset.model && ` ${asset.model}`}
                        {asset.serial_number && ` · S/N: ${asset.serial_number}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <ConditionBadge condition={asset.condition} />
                      <AssetStatusBadge status={asset.status} />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Signature Section */}
        <Card className="border-primary/20">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-primary" />
              <p className="text-sm font-semibold text-foreground">Customer Confirmation</p>
            </div>
            <p className="text-xs text-muted-foreground">
              By signing below, the customer confirms receipt of the items listed in this intake summary.
            </p>

            <div>
              <Label className="text-xs text-muted-foreground">Signer Name *</Label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Full name of person signing..."
                className="text-base h-12"
              />
            </div>

            <SignaturePad onSignatureChange={setSignatureData} label="Customer Signature *" />

            <p className="text-[10px] text-muted-foreground text-center">
              Signed at {new Date().toLocaleString()}
            </p>

            <Button
              onClick={handleConfirm}
              disabled={!signerName.trim() || !signatureData}
              className="w-full gap-2"
              size="lg"
            >
              <CheckCircle2 className="h-4 w-4" /> Confirm Intake
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
